import { ReactElement, useCallback } from 'react'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import useCornerConnectionAdditionalLoadProposalState from '@editor/pages/Results2/hooks/useCornerConnectionAdditionalLoadProposalState'
import { LoadingIndicator } from '@ui/feedback'
import { loadSources } from '../../AnchorDataGrid2/constants'
import CornerConnectionProposalForm from '../../CornerConnectionAdditionalLoadProposalForm'
import { AnchorManualLoadDraft, formatNewtonsAsKn, tryFormulaToNumber } from '../utils'

interface Props {
  supportGuid: string
  drafts: AnchorManualLoadDraft[]
  onChange: (drafts: AnchorManualLoadDraft[]) => void
  /** Appends a line to the anchor comment (used by the corner-connection helper). */
  onAppendComment: (comment: string) => void
  disabled: boolean
}

const sourceOptions = Object.entries(loadSources).filter(([key]) => key !== 'empty')

/** Index of the manual load row a corner-connection field name (`manual_load_N`) refers to. */
const fieldToIndex = (field: string): number => {
  const parsed = Number(field.split('_').pop())
  return Number.isFinite(parsed) && parsed > 0 ? parsed - 1 : 0
}

/**
 * Editable manual loads of one anchor (source + Excel-like formula in kN per row) with the
 * corner-connection helper ("Wand über Eck") rendered inline instead of as a separate pane.
 *
 * Edits stay local: the pane previews the resulting checks while the rows are valid, and only
 * "Speichern" in the footer persists them.
 */
const AnchorManualLoadsSection = ({
  supportGuid,
  drafts,
  onChange,
  onAppendComment,
  disabled,
}: Props): ReactElement => {
  const {
    cornerConnectionState,
    selectAnchor: startCornerSelection,
    abortHelper,
    fetchWithInterval,
    isLoadingWithInterval,
  } = useCornerConnectionAdditionalLoadProposalState()

  const cornerActiveForThisAnchor =
    cornerConnectionState.type !== 'idle' && cornerConnectionState.anchorGuid === supportGuid

  const updateRow = useCallback(
    (index: number, partial: Partial<AnchorManualLoadDraft>) => {
      onChange(drafts.map((draft, i) => (i === index ? { ...draft, ...partial } : draft)))
    },
    [drafts, onChange],
  )

  const handleAcceptCornerProposal = useCallback(
    (formula: string, _value: string, _rawFormula: string, maxForce: number) => {
      if (cornerConnectionState.type === 'idle') return
      const index = fieldToIndex(cornerConnectionState.field)
      const next = [...drafts]
      while (next.length <= index) next.push({ source: '', formula: '' })
      next[index] = { source: loadSources.wallCorner, formula }
      onChange(next)
      onAppendComment(`Eckverschraubung maximale Last: ${maxForce} kN`)
      abortHelper()
    },
    [abortHelper, cornerConnectionState, drafts, onAppendComment, onChange],
  )

  return (
    <Stack spacing={1} sx={{ pb: 1 }}>
      {drafts.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Keine manuellen Lasten
        </Typography>
      )}

      {drafts.map((draft, index) => {
        const value = tryFormulaToNumber(draft.formula)
        const formulaInvalid = !!draft.formula.trim() && value == null
        const rowIncomplete = !!draft.source !== !!draft.formula.trim()

        return (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <Select
              size="small"
              value={draft.source}
              onChange={event => updateRow(index, { source: event.target.value })}
              displayEmpty
              disabled={disabled}
              error={rowIncomplete && !draft.source}
              sx={{ width: 170, flexShrink: 0, fontSize: 13 }}
              data-cy={`anchor-manual-source-${index}`}
            >
              <MenuItem value="">
                <em>Herkunft wählen …</em>
              </MenuItem>
              {sourceOptions.map(([key, label]) => (
                <MenuItem key={key} value={label}>
                  {label}
                </MenuItem>
              ))}
            </Select>

            <TextField
              size="small"
              value={draft.formula}
              onChange={event => updateRow(index, { formula: event.target.value })}
              placeholder="Formel oder Wert in kN, z. B. =12*0,35"
              disabled={disabled}
              error={formulaInvalid || (rowIncomplete && !draft.formula.trim())}
              sx={{ flex: 1, '& input': { fontFamily: 'monospace', fontSize: 13 } }}
              data-cy={`anchor-manual-load-${index}`}
            />

            <Box
              sx={{
                width: 90,
                flexShrink: 0,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 13,
                color: formulaInvalid ? 'error.main' : 'text.secondary',
              }}
            >
              {formulaInvalid
                ? 'Fehler'
                : value == null
                ? '–'
                : `${formatNewtonsAsKn(value * 1000)} kN`}
            </Box>

            {draft.source === loadSources.wallCorner && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => startCornerSelection(supportGuid, `manual_load_${index + 1}`)}
                disabled={disabled || cornerConnectionState.type !== 'idle'}
                data-cy="choose-corner-wall-btn"
              >
                Wand auswählen
              </Button>
            )}

            <IconButton
              size="small"
              onClick={() => onChange(drafts.filter((_, i) => i !== index))}
              disabled={disabled}
              aria-label="Last entfernen"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        )
      })}

      <Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onChange([...drafts, { source: '', formula: '' }])}
          disabled={disabled}
          data-cy="anchor-add-manual-load-btn"
        >
          + Last hinzufügen
        </Button>
      </Box>

      {cornerActiveForThisAnchor && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
          {cornerConnectionState.type === 'selected-anchor' && (
            <Stack spacing={1}>
              <Alert severity="info">
                Wähle eine Wand in der Szene an, um die zusätzliche Auflast zu ermitteln.
              </Alert>
              <Box>
                <Button size="small" variant="outlined" onClick={abortHelper}>
                  Abbrechen
                </Button>
              </Box>
            </Stack>
          )}

          {cornerConnectionState.type === 'selected-anchor-and-wall' && <LoadingIndicator />}

          {(cornerConnectionState.type === 'proposal-created' ||
            cornerConnectionState.type === 'proposal-changed') && (
            <Stack spacing={1}>
              <CornerConnectionProposalForm
                anchorGuid={cornerConnectionState.anchorGuid}
                wallGuid={cornerConnectionState.wallGuid}
                initialData={cornerConnectionState.proposal}
                fetchWithInterval={fetchWithInterval}
                isLoadingWithInterval={isLoadingWithInterval}
                onAccept={handleAcceptCornerProposal}
              />
              <Box>
                <Button size="small" variant="outlined" onClick={abortHelper}>
                  Eingabehilfe schließen
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  )
}

export default AnchorManualLoadsSection
